import React, { useContext } from "react";
import { useTranslation } from "react-i18next";

import Grid from "@mui/material/Grid";
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";

import { RootContext } from "../../context/RootContext";
import { LoansContext } from "../../context/LoansContext";
import { Roles } from "../../utils/enums";

import WaiverSummary from "./WaiverSummary";
import GeneralWaiverRequestForm from "./GeneralWaiverRequestForm";
import { WaiverRequestData } from "./WaiverRequestForm";

// Loan-level General Waiver Request tab (TA-11746) — separate from the
// per-test waivers surfaced on individual failed-test cards in the Tests
// tab. Unlike per-test waivers, a loan can carry any number of these at
// once, so every entry in `generalWaiverDetails` is listed via the same
// WaiverSummary used for per-test waivers (it never renders
// takeoutPartner/testTitle).
//
// The "Request General Waiver" button itself lives at the page level (it's
// a page-wide sticky footer shared with the Tests tab, not scoped to this
// tab's own content) — isFormOpen/onCloseForm are lifted to the parent page
// so that shared button can open the form here directly.
export default function GeneralWaiverRequestTab({
  isFormOpen,
  onCloseForm,
}: {
  isFormOpen: boolean;
  onCloseForm: () => void;
}) {
  const { t } = useTranslation();
  const { userPermissions } = useContext(RootContext) ?? {};
  const { loanDetails } = useContext(LoansContext) ?? {};

  const canActOnWaiver = !!userPermissions?.includes(Roles.underwriter);

  const generalWaivers: WaiverRequestData[] = loanDetails?.generalWaiverDetails ?? [];

  return (
    <Grid container data-testid="general-waiver-request-tab">
      {isFormOpen ? (
        <Grid item xs={12}>
          <GeneralWaiverRequestForm onSubmit={onCloseForm} onCancel={onCloseForm} />
        </Grid>
      ) : null}

      {!isFormOpen && generalWaivers.length === 0 ? (
        <Grid item xs={12}>
          <div
            data-testid="no-general-waiver-added"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              padding: "24px",
              borderRadius: "6px",
              background: "#fff",
              boxShadow: "0 4px 14px 0 rgba(0, 0, 0, 0.14)",
            }}
          >
            <ChecklistOutlinedIcon style={{ color: "#655DBB" }} />
            <span
              style={{
                color: "#655DBB",
                fontFamily: "Poppins",
                fontSize: "20px",
                fontWeight: 500,
              }}
            >
              {t("noGeneralWaiverAdded")}
            </span>
          </div>
        </Grid>
      ) : null}

      {generalWaivers.map((waiver, index) => (
        <Grid item xs={12} key={waiver.id ?? index}>
          <WaiverSummary waiver={waiver} canActOnWaiver={canActOnWaiver} />
        </Grid>
      ))}
    </Grid>
  );
}
